import { useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import Avatar from "@shared/v2/Avatar";
import Button from "@shared/v2/Button";
import Dropdown from "@shared/v2/Dropdown";
import Modal from "@shared/v2/Modal";
import Typography from "@shared/v2/Typography";
import { LinkSolidV6 } from "@shared/v2/Icomoon";
import * as css from "../person-detail-styles.module.css";
import SearchPeople from "../../../QuickSearch/components/SearchPeople";

const AddRelationshipModal = ({
  closeAddRelationShipModal,
  person,
  saveRelationship,
  addRelationship,
  relationshipOptions,
}) => {
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedPersonId, setSelectedPersonId] = useState(null);
  const [relatedPerson, setRelatedPerson] = useState(undefined);
  const [primaryPersonLabel, setPrimaryPersonLabel] = useState(null);
  const [relatedPersonLabel, setRelatedPersonLabel] = useState(null);

  const onAddRelationship = (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("authenticity_token", ReactOnRails.authenticityToken());
    formData.append("relationship[person_y_id]", selectedPersonId);
    formData.append("person_details_page", "true");
    formData.append("view_type", "new");
    setLoading(true);
    axios
      .post(`/people/${person.data.attributes.slug}/relationships`, formData)
      .then((res) => {
        setRelatedPerson(res.data);
        addRelationship(res.data);
        setLoading(false);
        setPage(2);
      })
      .catch((err) => {
        // TODO: Handle error
        console.log(err);
        setLoading(false);
      });
  };

  const onSaveRelationship = (e) => {
    e.preventDefault();
    setLoading(true);
    saveRelationship(
      person.data.attributes,
      relatedPerson.id,
      primaryPersonLabel,
      relatedPersonLabel,
    ).finally(() => {
      setLoading(false);
      closeAddRelationShipModal();
    });
  };

  return (
    <Modal
      id="add-relationship-modal"
      show
      onHide={closeAddRelationShipModal}
      dialogClassName="tw-flex tw-items-center tw-justify-center tw-h-full"
      contentClassName="tw-max-w-[640px] tw-max-h-[90vh] tw-w-full tw-flex tw-flex-col tw-gap-[32px]"
    >
      <Modal.Header title={`${page === 1 ? "Add" : "Edit"} Relationship`} closeButton={!loading} />

      <Modal.Body className="tw-flex-1 tw-flex tw-flex-col tw-gap-[8px] tw-overflow-auto">
        {page === 1 && (
          <SearchPeople
            label="Search"
            excludeTrashAndArchived
            onSelectFunction={(p) => setSelectedPersonId(p.object_id)}
          />
        )}

        {page === 2 && (
          <div className="tw-flex">
            <div className="tw-basis-1/2 tw-flex tw-flex-col tw-gap-[16px] tw-pr-[8px] tw-relative tw-border-solid tw-border-l-0 tw-border-y-0 tw-border-r-[1px] tw-border-gray-10">
              <Typography variant="subtitle-m-bold">Primary Person</Typography>
              <div className="tw-absolute tw-bg-white tw-flex tw-justify-center tw-items-center tw-top-[52px] tw-right-[-15px] tw-w-[30px] tw-h-[56px]">
                <LinkSolidV6 className="tw-text-theme-primary" size="l" />
              </div>
              <div className="tw-flex tw-flex-col tw-gap-[8px] tw-items-center">
                <Avatar
                  src={person.data.attributes.avatar_url}
                  alt={
                    `${person.data.attributes.first_name} ${person.data.attributes.last_name}`.trim() ||
                    "No Name"
                  }
                  size="extra-large"
                />
                <span className="tw-text-18d tw-font-semibold">
                  {person.data.attributes.first_name} {person.data.attributes.last_name}
                </span>
              </div>
              <Dropdown
                label="Relationship"
                menuShouldComeToFront
                isSearchable
                isClearable={false}
                placeholder="Select..."
                value={relationshipOptions.find((o) => o.value === primaryPersonLabel) || null}
                options={relationshipOptions}
                onChange={(option) => setPrimaryPersonLabel(option.value)}
              />
            </div>
            <div className="tw-basis-1/2 tw-flex tw-flex-col tw-gap-[16px] tw-pl-[8px]">
              <Typography variant="subtitle-m-bold">Related Person</Typography>
              <div className="tw-flex tw-flex-col tw-gap-[8px] tw-items-center">
                <Avatar
                  src={relatedPerson.related_to_person_avatar_url}
                  alt={relatedPerson.related_person_name || "No Name"}
                  size="extra-large"
                />
                <span className="tw-text-18d tw-font-semibold">{relatedPerson.related_person_name}</span>
              </div>
              <Dropdown
                label="Relationship"
                menuShouldComeToFront
                isSearchable
                isClearable={false}
                placeholder="Select..."
                value={relationshipOptions.find((o) => o.value === relatedPersonLabel) || null}
                options={relationshipOptions}
                onChange={(option) => setRelatedPersonLabel(option.value)}
              />
              <div>
                <div className={css.brivityPersonContactLabel}>Email (Personal)</div>
                <div className="m-t-10">
                  <span className={css.brivityPersonContactIcon}>
                    <i className="fa fa-envelope-o" />
                  </span>
                  <span className={css.brivityPersonContactValue}>
                    {relatedPerson.related_to_person_email ? relatedPerson.related_to_person_email : "None"}
                  </span>
                </div>
              </div>
              <div>
                <div className={css.brivityPersonContactLabel}>Phone (Mobile)</div>
                <div className="m-t-10">
                  <span className={css.brivityPersonContactIcon}>
                    <i className="fa fa-phone" />
                  </span>
                  <span className={css.brivityPersonContactValue}>
                    {relatedPerson.related_to_person_phone ? relatedPerson.related_to_person_phone : "None"}
                  </span>
                </div>
              </div>
              <div>
                <div className={css.brivityPersonContactLabel}>Address (Home)</div>
                <div className="m-t-10">
                  <span className={css.brivityPersonContactIcon}>
                    <i className="fa fa-map-marker" />
                  </span>
                  <span className={css.brivityPersonContactValue}>
                    {relatedPerson.related_to_person_address && (
                      <span>
                        {relatedPerson.address?.street_address}
                        <br />
                        {relatedPerson.related_to_person_address?.city},{" "}
                        {relatedPerson.related_to_person_address?.locality}{" "}
                        {relatedPerson.related_to_person_address?.postal_code}
                      </span>
                    )}
                    {!relatedPerson.related_to_person_address && <span>None</span>}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="tw-flex tw-justify-between tw-gap-[16px]">
        <Button schema="tertiary" size="medium" onClick={closeAddRelationShipModal} disabled={loading}>
          Cancel
        </Button>
        <Button
          isLoading={loading}
          size="medium"
          disabled={page === 1 && !selectedPersonId}
          onClick={page === 1 ? onAddRelationship : onSaveRelationship}
        >
          {page === 1 ? "Next" : "Save"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

AddRelationshipModal.propTypes = {
  closeAddRelationShipModal: PropTypes.func.isRequired,
  addRelationship: PropTypes.func.isRequired,
  saveRelationship: PropTypes.func.isRequired,
  person: PropTypes.shape().isRequired,
  relationshipOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
};

export default AddRelationshipModal;
